// Deal types, stages and stage probabilities (Section 3.5).
// These are the seed values; the live, editable copy is the deal_stages table.
// A test checks that this file and the database seed stay identical.

export type DealType = 'tenant_rep' | 'll_rep' | 'listing_sale' | 'acquisition' | 'buyside';

export interface DealTypeInfo {
  key: DealType;
  label: string;
  isLease: boolean;
}

export const DEAL_TYPES: DealTypeInfo[] = [
  { key: 'tenant_rep', label: 'Tenant Rep', isLease: true },
  { key: 'll_rep', label: 'LL Rep', isLease: true },
  { key: 'listing_sale', label: 'Sale', isLease: false },
  { key: 'acquisition', label: 'Acquisition', isLease: false },
  { key: 'buyside', label: 'Buyside', isLease: false },
];

export function isLeaseDealType(t: DealType): boolean {
  return t === 'tenant_rep' || t === 'll_rep';
}

export interface StageInfo {
  key: string;
  label: string;
  /** 0–1 */
  probability: number;
  /** Closing stages: only reachable once every payment is received. */
  isClosed: boolean;
}

const s = (key: string, label: string, probability: number, isClosed = false): StageInfo => ({
  key,
  label,
  probability,
  isClosed,
});

const LEASE_TAIL: StageInfo[] = [
  s('lois', 'LOIs', 0.5),
  s('lease_negotiation', 'Lease Negotiation', 0.6),
  s('lease_signed', 'Lease Signed', 0.75),
  s('occupancy', 'Occupancy', 0.9),
  s('closed_fully_paid', 'Closed / Fully Paid', 1, true),
];

const PURCHASE_STAGES: StageInfo[] = [
  s('search', 'Search', 0.25),
  s('lois', 'LOIs', 0.5),
  s('contract_negotiation', 'Contract Negotiation', 0.6),
  s('under_contract', 'Under Contract', 0.75),
  s('closed', 'Closed', 1, true),
];

export const STAGES: Record<DealType, StageInfo[]> = {
  tenant_rep: [s('search', 'Search', 0.25), ...LEASE_TAIL],
  ll_rep: [s('listing', 'Listing', 0.4), ...LEASE_TAIL],
  listing_sale: [
    s('bov', 'BOV', 0.25),
    s('listing_marketing', 'Listing / Marketing', 0.4),
    s('lois', 'LOIs', 0.5),
    s('contract_negotiation', 'Contract Negotiation', 0.6),
    s('under_contract', 'Under Contract', 0.75),
    s('closed', 'Closed', 1, true),
  ],
  acquisition: PURCHASE_STAGES,
  buyside: PURCHASE_STAGES,
};

export function stageProbability(dealType: DealType, stageKey: string): number {
  const stage = STAGES[dealType].find((x) => x.key === stageKey);
  if (!stage) throw new Error(`Unknown stage "${stageKey}" for deal type ${dealType}`);
  return stage.probability;
}
